<template>
  <nav class="auth-navbar">
    <div class="navbar-container">
      <!-- Logo Section -->
      <div class="navbar-brand">
        <router-link to="/" class="logo-link">
          <img
            alt="Carenuity logo"
            src="./../../assets/logo-inner.png"
            class="logo-img"
            height="50"
          />
        </router-link>
      </div>

      <!-- Actions -->
      <div class="navbar-actions">
        <template v-if="isAuthenticated">
          <router-link to="/dashboard" class="auth-link">Dashboard</router-link>
          <router-link to="/devices" class="auth-link">Devices</router-link>
          <router-link to="/alarms" class="auth-link">Alarms</router-link>
          <router-link to="/settings" class="auth-link">Settings</router-link>
          <button class="auth-link" @click="logout">Logout</button>
        </template>
        <template v-else>
          <router-link to="/auth" class="auth-link">Log In / Sign Up</router-link>
        </template>
      </div>
    </div>
  </nav>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { useAuth } from '@/composables/core/useAuth';

export default defineComponent({
  name: 'TheAuthNavbar',
  setup() {
    // Composables
    const { isAuthenticated, logout: authLogout } = useAuth();

    // Methods
    const logout = async (): Promise<void> => {
      await authLogout();
      
      // Wait for next tick to ensure Vuex state has propagated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Force page reload to ensure clean state
      window.location.href = '/auth';
    };

    return {
      isAuthenticated,
      logout
    };
  }
});
</script>

<style scoped>
/* ============================================
   Auth Navbar Base
   ============================================ */
.auth-navbar {
  background: var(--gradient-primary);             /* was: linear-gradient(...) */
  box-shadow: var(--shadow-md);                    /* was: 0 2px 12px rgba(0, 0, 0, 0.1) */
  position: sticky;
  top: 0;
  z-index: 1000;
  width: 100%;
}

.navbar-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--spacing-6);                     /* was: 0 1.5rem */
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 70px;
}

/* ============================================
   Logo Section
   ============================================ */
.navbar-brand {
  flex-shrink: 0;
}

.logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.logo-img {
  height: 50px;
  width: auto;
  transition: var(--transition-base);              /* was: transform 0.3s ease */
}

.logo-img:hover {
  transform: scale(1.05);
}

/* ============================================
   Auth Actions
   ============================================ */
.navbar-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);                           /* was: 1rem */
}

.auth-link {
  display: inline-block;
  padding: var(--spacing-2_5) var(--spacing-6);    /* was: 0.65rem 1.5rem */
  color: white;
  text-decoration: none;
  font-weight: var(--font-weight-semibold);        /* was: 600 */
  font-size: var(--font-size-base);                /* was: 0.95rem */
  border-radius: var(--radius-md);                 /* was: 8px */
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: var(--transition-base);              /* was: all 0.3s ease */
}

.auth-link:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.auth-link:active {
  transform: translateY(0);
}

/* ============================================
   Responsive Design - Mobile
   ============================================ */
@media (max-width: 768px) {
  .navbar-container {
    min-height: 60px;
    padding: 0 var(--spacing-4);                   /* was: 0 1rem */
  }

  .logo-img {
    height: 40px;
  }

  .auth-link {
    padding: var(--spacing-2) var(--spacing-4);    /* was: 0.5rem 1rem */
    font-size: var(--font-size-sm);                /* was: 0.85rem */
  }
}

@media (max-width: 480px) {
  .auth-link {
    padding: var(--spacing-2) var(--spacing-3);    /* was: 0.5rem 0.75rem */
    font-size: var(--font-size-sm);                /* was: 0.8rem */
  }
}
</style>
